import { useMemo, useState } from 'react';
import {
  Alert, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Stack, TextField, Typography,
} from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { usePageTitle } from '../../components/shell/AppShell';
import { RouteDataGrid } from '../../components/data-grid/RouteDataGrid';
import type { QuickFilter, RowAction, SearchField } from '../../components/data-grid/RouteDataGrid';
import { EntityDetailDialog } from '../../components/dialogs/EntityDetailDialog';
import type { DetailRow } from '../../components/dialogs/EntityDetailDialog';
import { ConfirmDialog } from '../../components/dialogs/ConfirmDialog';
import { fmtDateTime, fmtOrEmDash } from '../../utils/format';
import type { ExportColumn } from '../../utils/export';
import type { CashDeskTerminal } from '../../api/types';
import { useCashDeskTerminalAction, useCashDeskTerminals } from '../../api/queries';
import { SitePicker } from './SitePicker';

/**
 * Cash-desk terminal registry (plan Draft 1 W3). Terminals are
 * RM-authorized hardware: an unseen browser at a site's cash desk shows a
 * pairing code, which appears here as a PENDING row. Approve binds a name
 * and activates it; Revoke kills it on the SC's next config sync.
 * Credential material never reaches this surface.
 */
export default function CashDeskTerminalsPage() {
  usePageTitle('Cash desk terminals');
  const [siteId, setSiteId] = useState<string | null>(null);
  return (
    <Stack spacing={2}>
      <Typography variant="h4">Cash desk terminals</Typography>
      <SitePicker siteId={siteId} onChange={setSiteId} />
      {siteId && <CashDeskTerminalsPanel siteId={siteId} />}
    </Stack>
  );
}

/** Site-scoped terminal registry — embedded by the Sites page's tabbed
 *  detail view and wrapped with a picker by the standalone page above. */
export function CashDeskTerminalsPanel({ siteId }: { siteId: string }) {
  const { queryKey, listFn } = useCashDeskTerminals(siteId);
  const action = useCashDeskTerminalAction(siteId);

  const [detailRow, setDetailRow] = useState<CashDeskTerminal | null>(null);
  const [approveRow, setApproveRow] = useState<CashDeskTerminal | null>(null);
  const [approveName, setApproveName] = useState('');
  const [revokeRow, setRevokeRow] = useState<CashDeskTerminal | null>(null);

  const statusChip = (t: CashDeskTerminal) => (
    <Chip
      size="small"
      label={t.status === 'pending' && t.pairingExpired ? 'pending (expired)' : t.status}
      color={t.status === 'active' ? 'success' : t.status === 'pending' ? 'warning' : 'default'}
    />
  );

  const columns = useMemo<GridColDef<CashDeskTerminal>[]>(() => [
    { field: 'name', headerName: 'Terminal', flex: 1.3,
      renderCell: (p) => (
        <Stack sx={{ py: 0.5 }}>
          <Typography variant="body2">{p.row.name ?? 'New terminal'}</Typography>
          <Typography variant="caption" color="text.secondary">
            {p.row.status === 'pending' ? 'awaiting approval' : fmtOrEmDash(p.row.credentialKind)}
          </Typography>
        </Stack>
      ) },
    { field: 'status', headerName: 'Status', width: 150,
      renderCell: (p) => statusChip(p.row) },
    { field: 'pairingCode', headerName: 'Pairing code', width: 150,
      renderCell: (p) => p.row.pairingCode
        ? <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, letterSpacing: 2 }}>
            {p.row.pairingCode}
          </Typography>
        : <>—</> },
    { field: 'requestedAt', headerName: 'Requested', width: 180,
      valueFormatter: (value) => fmtDateTime(value) },
    { field: 'approvedAt', headerName: 'Approved', width: 180,
      valueFormatter: (value) => (value ? fmtDateTime(value) : '—') },
  ], []);

  const searchFields = useMemo<SearchField[]>(() => [
    { kind: 'text',   key: 'name',   label: 'Terminal name' },
    { kind: 'select', key: 'status', label: 'Status', options: [
      { value: 'pending', label: 'Pending' },
      { value: 'active',  label: 'Active' },
      { value: 'revoked', label: 'Revoked' },
    ] },
  ], []);

  const quickFilters = useMemo<QuickFilter<CashDeskTerminal>[]>(() => [
    { label: 'All',     filter: () => true },
    { label: 'Pending', filter: (r) => r.status === 'pending' },
    { label: 'Active',  filter: (r) => r.status === 'active' },
    { label: 'Revoked', filter: (r) => r.status === 'revoked' },
  ], []);

  const exportColumns = useMemo<ExportColumn<CashDeskTerminal>[]>(() => [
    { key: 'name',           label: 'Terminal',       format: (v) => fmtOrEmDash(v) },
    { key: 'status',         label: 'Status' },
    { key: 'credentialKind', label: 'Credential',     format: (v) => fmtOrEmDash(v) },
    { key: 'userAgent',      label: 'Device',         format: (v) => fmtOrEmDash(v) },
    { key: 'requestedAt',    label: 'Requested',      format: (v) => fmtDateTime(v) },
    { key: 'approvedAt',     label: 'Approved',       format: (v) => (v ? fmtDateTime(v) : '—') },
    { key: 'revokedAt',      label: 'Revoked',        format: (v) => (v ? fmtDateTime(v) : '—') },
  ], []);

  const actions = useMemo<RowAction<CashDeskTerminal>[]>(() => [
    { label: 'View', onClick: (row) => setDetailRow(row) },
    {
      label: 'Approve',
      visible: (row) => row.status === 'pending' && !row.pairingExpired,
      onClick: (row) => { setApproveName(''); setApproveRow(row); },
    },
    {
      label: 'Revoke',
      destructive: true,
      visible: (row) => row.status !== 'revoked',
      onClick: (row) => setRevokeRow(row),
    },
  ], []);

  const detailFields = useMemo<DetailRow<CashDeskTerminal>[]>(() => [
    { label: 'Terminal',     render: (r) => r.name ?? '—' },
    { label: 'Status',       render: (r) => statusChip(r) },
    { label: 'Pairing code', render: (r) => r.pairingCode
        ? <Typography component="span" sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 22, letterSpacing: 4 }}>{r.pairingCode}</Typography>
        : '—' },
    { label: 'Pairing expires', render: (r) => (r.pairingExpiresAt ? fmtDateTime(r.pairingExpiresAt) : '—') },
    { label: 'Credential',   render: (r) => fmtOrEmDash(r.credentialKind) },
    { label: 'Device',       render: (r) => fmtOrEmDash(r.userAgent) },
    // Registry lifecycle trail (this entity's audit block).
    { label: 'Requested',    render: (r) => fmtDateTime(r.requestedAt) },
    { label: 'Approved',     render: (r) => (r.approvedAt ? fmtDateTime(r.approvedAt) : '—') },
    { label: 'Revoked',      render: (r) => (r.revokedAt ? fmtDateTime(r.revokedAt) : '—') },
  ], []);

  return (
    <Stack spacing={2}>
      <Alert severity="info">
        Only terminals approved here can ever act as a cash desk. A new device shows a
        pairing code on its screen — match it against the pending row before approving.
      </Alert>

      <RouteDataGrid<CashDeskTerminal>
        entityName="Cash desk terminals"
        queryKey={queryKey}
        listFn={listFn}
        columns={columns}
        searchFields={searchFields}
        quickFilters={quickFilters}
        exportColumns={exportColumns}
        reportTitle="ARMS — Cash desk terminals"
        actions={actions}
        onRowClick={(row) => setDetailRow(row)}
        emptyMessage="No terminals yet. Open the cash desk on a device at this site to start enrolment."
      />

      <EntityDetailDialog<CashDeskTerminal>
        open={detailRow !== null}
        title={detailRow?.name ?? 'Terminal'}
        data={detailRow}
        fields={detailFields}
        onClose={() => setDetailRow(null)}
      />

      <Dialog open={approveRow !== null} onClose={() => setApproveRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Approve terminal</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2">
              Confirm the device shows pairing code{' '}
              <Typography component="span" sx={{ fontFamily: 'monospace', fontWeight: 700, letterSpacing: 2 }}>
                {approveRow?.pairingCode}
              </Typography>{' '}
              before approving.
            </Typography>
            <TextField
              label="Terminal name"
              required
              autoFocus
              value={approveName}
              onChange={(e) => setApproveName(e.target.value)}
              helperText="Shown everywhere this terminal appears, e.g. 'Front desk tablet'."
              inputProps={{ 'data-testid': 'terminal-approve-name' }}
            />
            {action.isError && (
              <Alert severity="error">
                {(action.error as { message?: string })?.message ?? 'Approval failed.'}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveRow(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={approveName.trim() === '' || action.isPending}
            data-testid="terminal-approve-submit"
            onClick={() => {
              if (!approveRow) return;
              action.mutate(
                { terminalId: approveRow.id, action: 'approve', name: approveName.trim() },
                { onSuccess: () => setApproveRow(null) },
              );
            }}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={revokeRow !== null}
        title="Revoke terminal"
        message={revokeRow ? (
          <>Revoke <strong>{revokeRow.name ?? 'this terminal'}</strong>? It stops working at the
          site's next config sync and can only return through a fresh enrolment.</>
        ) : ''}
        confirmLabel="Revoke"
        destructive
        busy={action.isPending}
        error={revokeRow && action.isError
          ? ((action.error as { message?: string })?.message ?? 'Revoke failed.')
          : undefined}
        onConfirm={() => {
          if (!revokeRow) return;
          action.mutate(
            { terminalId: revokeRow.id, action: 'revoke' },
            { onSuccess: () => setRevokeRow(null) },
          );
        }}
        onCancel={() => setRevokeRow(null)}
      />
    </Stack>
  );
}
